// Unified dashboard overview data.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getDashboardOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    // Buyer's buy_orders
    const myOrdersRes = await supabase
      .from("buy_orders")
      .select("id, title, card_name, tcg, condition_min, condition_type, status, max_price_cents, updated_at, created_at")
      .eq("buyer_id", userId)
      .order("updated_at", { ascending: false })
      .limit(10);
    const recentBuyOrders = (myOrdersRes.data ?? []) as any[];
    const myOrderIdsForOffers = recentBuyOrders.map((o) => o.id as string);

    // Offer counts per order
    const offerCountMap = new Map<string, number>();
    if (myOrderIdsForOffers.length) {
      const r = await supabase
        .from("offers")
        .select("buy_order_id")
        .in("buy_order_id", myOrderIdsForOffers);
      for (const row of r.data ?? []) {
        const k = row.buy_order_id as string;
        offerCountMap.set(k, (offerCountMap.get(k) ?? 0) + 1);
      }
    }
    const recentBuyOrdersEnriched = recentBuyOrders.map((o) => ({
      ...o,
      offers_received: offerCountMap.get(o.id) ?? 0,
    }));

    // Buying counters (all-time, filtered by status)
    const [
      activeBuyingRes,
      offersReceivedRes,
      buyerPendingConfirmTxnsRes,
      inTransitRes,
    ] = await Promise.all([
      supabase.from("buy_orders").select("id", { count: "exact", head: true }).eq("buyer_id", userId).eq("status", "open"),
      (async () => {
        const idsRes = await supabase.from("buy_orders").select("id").eq("buyer_id", userId);
        const ids = (idsRes.data ?? []).map((r) => r.id as string);
        if (!ids.length) return { count: 0, error: null } as any;
        return supabase
          .from("offers")
          .select("id", { count: "exact", head: true })
          .in("buy_order_id", ids)
          .in("status", ["pending", "pending_buyer_confirmation"]);
      })(),
      supabase
        .from("transactions")
        .select("id")
        .eq("buyer_id", userId)
        .in("status", ["shipped", "delivered"]),
      supabase.from("transactions").select("id", { count: "exact", head: true }).eq("buyer_id", userId).in("status", ["shipped"]),
    ]);

    // Selling counters
    const [
      offersSubmittedRes,
      offersAcceptedRes,
      photosRequiredRes,
      itemsToShipRes,
      sellerPendingConfirmTxnsRes,
      completedSalesRes,
    ] = await Promise.all([
      supabase.from("offers").select("id", { count: "exact", head: true }).eq("seller_id", userId),
      supabase.from("offers").select("id", { count: "exact", head: true }).eq("seller_id", userId).in("status", ["accepted", "pending_buyer_confirmation"]),
      supabase.from("offers").select("id", { count: "exact", head: true }).eq("seller_id", userId).eq("status", "accepted"),
      supabase.from("transactions").select("id", { count: "exact", head: true }).eq("seller_id", userId).eq("status", "funds_held"),
      supabase
        .from("transactions")
        .select("id")
        .eq("seller_id", userId)
        .in("status", ["shipped", "delivered"]),
      supabase.from("transactions").select("id", { count: "exact", head: true }).eq("seller_id", userId).eq("status", "released"),
    ]);

    // Count txns awaiting mutual confirmation (not yet mutually confirmed, not expired).
    async function countPendingConfirmations(txnIds: string[]): Promise<number> {
      if (!txnIds.length) return 0;
      const r = await supabase
        .from("transaction_completions")
        .select("transaction_id, mutual_confirmed_at, fourteen_day_expired_at")
        .in("transaction_id", txnIds);
      const byId = new Map((r.data ?? []).map((row: any) => [row.transaction_id as string, row]));
      let pending = 0;
      for (const id of txnIds) {
        const row: any = byId.get(id);
        if (!row) { pending += 1; continue; }
        if (!row.mutual_confirmed_at && !row.fourteen_day_expired_at) pending += 1;
      }
      return pending;
    }

    const buyerPendingConfirmation = await countPendingConfirmations(
      (buyerPendingConfirmTxnsRes.data ?? []).map((r: any) => r.id as string),
    );
    const sellerPendingConfirmation = await countPendingConfirmations(
      (sellerPendingConfirmTxnsRes.data ?? []).map((r: any) => r.id as string),
    );

    // Recent sale offers (as seller) with buy_order enrichment
    const myOffersRes = await supabase
      .from("offers")
      .select("id, buy_order_id, price_cents, status, currency, updated_at, created_at")
      .eq("seller_id", userId)
      .order("updated_at", { ascending: false })
      .limit(10);
    const myOffers = (myOffersRes.data ?? []) as any[];
    const offerOrderIds = Array.from(
      new Set(myOffers.map((o) => o.buy_order_id as string).filter(Boolean)),
    );
    let orderLookup = new Map<string, { card_name: string; max_price_cents: number }>();
    if (offerOrderIds.length) {
      const r = await supabase
        .from("buy_orders")
        .select("id, card_name, max_price_cents")
        .in("id", offerOrderIds);
      orderLookup = new Map(
        (r.data ?? []).map((row) => [
          row.id as string,
          { card_name: row.card_name as string, max_price_cents: row.max_price_cents as number },
        ]),
      );
    }
    const recentSaleOffers = myOffers.map((o) => {
      const bo = orderLookup.get(o.buy_order_id);
      return {
        id: o.id,
        buy_order_id: o.buy_order_id,
        price_cents: o.price_cents,
        status: o.status,
        currency: o.currency,
        updated_at: o.updated_at,
        card_name: bo?.card_name ?? "Card",
        buyer_target_cents: bo?.max_price_cents ?? null,
      };
    });

    // Profile for greeting, trusted seller status, verified store
    const profileRes = await supabase
      .from("profiles")
      .select("username, display_name, avatar_url, trusted_seller_since, verified_store, verified_since, store_name")
      .eq("id", userId)
      .maybeSingle();

    return {
      counts: {
        buying: {
          active: activeBuyingRes.count ?? 0,
          offers_received: (offersReceivedRes as any).count ?? 0,
          pending_confirmation: buyerPendingConfirmation,
          in_transit: inTransitRes.count ?? 0,
        },
        selling: {
          offers_submitted: offersSubmittedRes.count ?? 0,
          offers_accepted: offersAcceptedRes.count ?? 0,
          photos_required: photosRequiredRes.count ?? 0,
          items_to_ship: itemsToShipRes.count ?? 0,
          pending_confirmation: sellerPendingConfirmation,
          completed_sales: completedSalesRes.count ?? 0,
        },
      },
      recentBuyOrders: recentBuyOrdersEnriched,
      recentSaleOffers,
      profile: profileRes.data as any,
    };
  });
